import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import StoneXChart from '../../../StoneX/StoneXChart';
import { readCssColorVariable, readDecileColor } from '../DecileMatrixStyles';
import { MatrixBand } from '../HedgePlans/HedgePlanMatrixBands';
import { SeasonalMatrixPoint, SeasonalMatrixYearPrice } from './SeasonalMatrixTypes';

interface SeasonalMatrixChartProps {
  title: string;
  currentYear: SeasonalMatrixPoint[];
  threeYearAverage: SeasonalMatrixPoint[];
  fiveYearAverage: SeasonalMatrixPoint[];
  forecastAverage: number | null;
  currencySymbol: string;
  // Plots the 3 and 5 year averages against their own axis on the right.
  averagesOnSecondAxis: boolean;
  // The decile bands to shade on the price axis.
  bands: MatrixBand[];
  // The current year's price under the cursor, or undefined when the cursor leaves the line.
  onHoveredPriceChange: (price: number | undefined) => void;
  onChartReady: (chart: Highcharts.Chart) => void;
}

// The comparison lines are faded and dashed so the current year stands out.
const comparisonLineOpacity = 0.7;
const decileBandId = 'seasonal-matrix-decile-band';

function toSeriesData(points: SeasonalMatrixPoint[]): Highcharts.PointOptionsObject[] {
  return points.map((point) => ({ x: point.position, y: point.price, custom: { yearPrices: point.yearPrices } }));
}

// The axis counts trading days, so each month is labelled at its first trading day. A day takes its month from
// this year's date where there is one, and otherwise from the latest past year's date for that day.
function getMonthStartTicks(currentYear: SeasonalMatrixPoint[], fiveYearAverage: SeasonalMatrixPoint[]): Map<number, string> {
  const monthByPosition = new Map<number, number>();
  [...fiveYearAverage, ...currentYear].forEach((point) => {
    const latestYearPrice = point.yearPrices[point.yearPrices.length - 1];
    if (latestYearPrice) {
      monthByPosition.set(point.position, dayjs(latestYearPrice.date).month());
    }
  });

  const monthStartTicks = new Map<number, string>();
  let previousMonth = -1;
  Array.from(monthByPosition.entries())
    .sort(([left], [right]) => left - right)
    .forEach(([position, month]) => {
      if (month !== previousMonth) {
        monthStartTicks.set(position, dayjs().month(month).format('MMM'));
        previousMonth = month;
      }
    });

  return monthStartTicks;
}

export default function SeasonalMatrixChart(props: SeasonalMatrixChartProps) {
  const { title, currentYear, threeYearAverage, fiveYearAverage, forecastAverage, currencySymbol, averagesOnSecondAxis, bands, onHoveredPriceChange, onChartReady } = props;
  // Read through a ref so a new handler does not rebuild the chart.
  const hoveredPriceHandler = useRef(onHoveredPriceChange);
  hoveredPriceHandler.current = onHoveredPriceChange;
  const chartRef = useRef<Highcharts.Chart | null>(null);

  const handleChartReady = useCallback((chart: Highcharts.Chart) => {
    chartRef.current = chart;
    onChartReady(chart);
  }, [onChartReady]);

  const options = useMemo<Highcharts.Options>(() => {
    const formatPrice = (value: number) => `${currencySymbol}${value.toFixed(4)}`;
    const fadeColor = (color: string) => new Highcharts.Color(color).setOpacity(comparisonLineOpacity).get('rgba') as string;
    const forecastColor = fadeColor(readCssColorVariable('--seasonal-matrix-forecast-line'));
    const priceAxisLabels: Highcharts.YAxisLabelsOptions = {
      formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
        return `${currencySymbol}${parseFloat(`${this.value}`).toFixed(2)}`;
      },
    };
    const averagesAxis = averagesOnSecondAxis ? 1 : 0;
    const monthStartTicks = getMonthStartTicks(currentYear, fiveYearAverage);

    const yAxis: Highcharts.YAxisOptions[] = [{
      title: { text: undefined },
      labels: priceAxisLabels,
      // A plot line does not stretch the axis, so make sure the forecast is always in view.
      softMin: forecastAverage ?? undefined,
      softMax: forecastAverage ?? undefined,
      plotLines: forecastAverage == null ? [] : [{
        value: forecastAverage,
        color: forecastColor,
        dashStyle: 'Dash',
        width: 2,
        zIndex: 3,
        label: { text: `Forecast ${formatPrice(forecastAverage)}`, style: { color: forecastColor } },
      }],
    }];

    if (averagesOnSecondAxis) {
      yAxis.push({ title: { text: undefined }, labels: priceAxisLabels, opposite: true, gridLineWidth: 0 });
    }

    return {
      title: { text: title },
      credits: { enabled: false },
      xAxis: {
        title: { text: undefined },
        tickPositions: Array.from(monthStartTicks.keys()),
        labels: {
          align: 'left',
          x: 4,
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
            return monthStartTicks.get(Number(this.value)) ?? '';
          },
        },
      },
      yAxis: yAxis,
      legend: { enabled: true },
      plotOptions: {
        line: {
          marker: { enabled: false },
          // The tooltip is shared, so the hovered point can belong to any line. Report the current year's price on that trading day.
          point: {
            events: {
              mouseOver: function (this: Highcharts.Point) {
                const currentYearPoint = this.series.chart.series[0]?.points.find((point) => point.x === this.x);
                hoveredPriceHandler.current(currentYearPoint?.y ?? undefined);
              },
            },
          },
          events: { mouseOut: () => hoveredPriceHandler.current(undefined) },
        },
      },
      series: [
        {
          type: 'line',
          name: 'Current Year',
          data: toSeriesData(currentYear),
          color: readCssColorVariable('--seasonal-matrix-current-year-line'),
          lineWidth: 3,
          zIndex: 2,
        },
        { type: 'line', name: '3 Year Average', data: toSeriesData(threeYearAverage), color: fadeColor(readCssColorVariable('--seasonal-matrix-three-year-line')), dashStyle: 'Dash', yAxis: averagesAxis },
        { type: 'line', name: '5 Year Average', data: toSeriesData(fiveYearAverage), color: fadeColor(readCssColorVariable('--seasonal-matrix-five-year-line')), dashStyle: 'Dash', yAxis: averagesAxis },
      ],
      tooltip: {
        shared: true,
        // Each line lists the years behind it with their own dates, since the Nth trading day falls on a different date each year.
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const lines = (this.points ?? []).map((point) => {
            const yearPrices: SeasonalMatrixYearPrice[] = (point.point.options.custom as { yearPrices?: SeasonalMatrixYearPrice[] } | undefined)?.yearPrices ?? [];
            const details = yearPrices.map((yearPrice) => `&nbsp;&nbsp;${dayjs(yearPrice.date).format('D MMM YYYY')}: ${formatPrice(yearPrice.price)}`);
            return [`<span style="color:${point.color}">●</span> <b>${point.series.name}: ${formatPrice(point.y ?? 0)}</b>`, ...details].join('<br/>');
          });
          return [`Trading day ${this.x}`, ...lines].join('<br/>');
        },
      },
    };
  }, [title, currentYear, threeYearAverage, fiveYearAverage, forecastAverage, currencySymbol, averagesOnSecondAxis]);

  // The bands change on every hover, so they are drawn on the chart directly. Passing them in the options
  // would redraw the series, which drops the hover and puts the bands straight back.
  useEffect(() => {
    const priceAxis = chartRef.current?.yAxis[0];
    if (!priceAxis) {
      return;
    }

    priceAxis.removePlotBand(decileBandId);
    bands.forEach((band) => {
      const bandColor = readDecileColor(band.percentile);
      priceAxis.addPlotBand({
        id: decileBandId,
        from: band.lowValue,
        to: band.highValue,
        color: `${bandColor}33`,
        borderColor: bandColor,
        borderWidth: 1,
        label: { text: band.label, style: { color: 'var(--font-color)', fontWeight: '600', fontSize: '11px' } },
      });
    });
  }, [bands, options]);

  return <StoneXChart highcharts={Highcharts} options={options} onChartReady={handleChartReady} />;
}
